/**
 * The object check of the agent service, an experiment: the judge tells whether the best shot of
 * a hit of a text search of objects matches the text (POST /agent/api/v1/search/check-objects,
 * `objectcheck.Result` in Go). state/ui/objectVerificationAtoms.ts decides what a panel asks
 * the judge and what it shows.
 */
import { z } from 'zod';

import { keyBy, listOf } from '@/api/listOf';

import type { ObjectEvent, ObjectsSort } from './objects';

/** The judge's verdict. A frozen object, because the value arrives in JSON. */
export const ObjectVerdict = {
    Match: 'match',
    Mismatch: 'mismatch',
    Unclear: 'unclear',
} as const;

export type ObjectVerdict = (typeof ObjectVerdict)[keyof typeof ObjectVerdict];

/** The check of a hit before the judge's verdict. */
export enum ObjectCheckProgress {
    Sent = 'sent',
    /** The check got no verdict. A Retry, or a read after its readers left, sends it again. */
    Failed = 'failed',
}

/** The hits that the judge checks, with the text and the exclusion of their search. */
export interface ObjectCheckRequest {
    query: string;
    exclusion?: string;
    /** The score goes only to the agent's log, beside the verdict. */
    objects: { eventId: string; similarity: number }[];
}

/** The judge's verdict on one hit. */
export const ObjectCheckVerdictSchema = z.object({
    eventId: z.string(),
    verdict: z.enum([ObjectVerdict.Match, ObjectVerdict.Mismatch, ObjectVerdict.Unclear]),
    /** The probability of a match grades a hit more finely than the verdict. */
    probabilities: z.object({ match: z.number() }),
});

export type ObjectCheckVerdict = z.infer<typeof ObjectCheckVerdictSchema>;

export type ObjectCheckState = ObjectCheckVerdict | ObjectCheckProgress;

/** A verdict for each hit of the check that has a best shot and an answer. */
export const ObjectCheckResultSchema = z.object({
    results: listOf(ObjectCheckVerdictSchema, 'results', { getKey: keyBy('eventId') }),
});

export type ObjectCheckResult = z.infer<typeof ObjectCheckResultSchema>;

export interface VerificationCandidate {
    eventId: string;
    similarity: number;
}

/** One object of a text search that the judge checks. The score goes only to the agent's log. */
export interface CheckedObject extends VerificationCandidate {
    query: string;
    exclusion: string | null;
}

/** The search that a panel shows, as the judge needs it. */
export interface VerificationSearch {
    searchKey: string;
    query: string;
    exclusion: string | null;
    hits: readonly ObjectEvent[];
    isLoading: boolean;
    isRanked: boolean;
    sortBy: ObjectsSort;
    sliderValue: number;
    isEnabled: boolean;
    dayZone: string;
}

/** What a panel shows of its search while the judge works. */
export interface ObjectVerification {
    searchKey: string;
    hits: readonly ObjectEvent[];
    minSimilarity: number;
    isVerifying: boolean;
    hasUncheckedDays: boolean;
    dayCount: number;
    areDaysGated: boolean;
    dayZone: string;
}

/** A change to the verification of the search of a panel. */
export enum VerificationChange {
    /** The search that the panel shows. */
    Search = 'search',
    /** In the order by time, the judge checks the hits of one more day. */
    CheckNextDay = 'checkNextDay',
    /** Sends the unanswered checks of the search again. */
    Retry = 'retry',
    /** The panel left: nothing more goes to the judge for it. */
    Release = 'release',
}

export type VerificationAction =
    | ({ kind: VerificationChange.Search } & VerificationSearch) |
    // The days that the panel saw: a second request for the same day opens no more days.
    { kind: VerificationChange.CheckNextDay; dayCount: number } |
    { kind: VerificationChange.Retry } |
    { kind: VerificationChange.Release };
